import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { DataTable, inferColumns, LoadMore, type Column } from '@/components/data/data-table';
import { Value } from '@/components/data/value';
import { SchemaForm, cleanValues, initialValues, validate, type FieldOverride, type FormValues } from '@/components/forms/schema-form';
import { Button } from '@/components/ui/button';
import { Badge, EmptyState, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/misc';
import { Sheet, useConfirm } from '@/components/ui/overlay';
import { DescriptionList, PageHeader } from '@/components/ui/page';
import { api, ApiError, fillPath, rows as toRows, type Query } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { deleteIsArchive, hasOperation, idempotent, itemActions, requestSchema, spec } from '@/lib/openapi';
import { errorMessage, useList } from '@/lib/query';
import { humanize } from '@/lib/utils';
import { ActionButton } from './action-runner';
import { defaultQuery, FiltersBar } from './filters';

export interface ResourceConfig {
  title: string;
  description?: string;
  /** List + create endpoint, e.g. `admin/services`. */
  path: string;
  /** Detail/update/delete endpoint; defaults to `<path>/{id}`. */
  itemPath?: string;
  columns?: Array<Column<Record<string, unknown>>>;
  /** Title of a record in the drawer. */
  rowTitle?(row: Record<string, unknown>): ReactNode;
  overrides?: Record<string, FieldOverride>;
  /** Hide generic capabilities a page replaces with its own UI. */
  hideCreate?: boolean;
  hideEdit?: boolean;
  hideDelete?: boolean;
  /** Item actions (`<path>/{id}/<verb>`) not to show. */
  excludeActions?: string[];
  /** Extra content in the record drawer. */
  renderDetail?(row: Record<string, unknown>, refresh: () => void): ReactNode;
  /** Extra header actions. */
  headerActions?: ReactNode;
  defaultFilters?: Query;
  createLabel?: string;
  emptyHint?: string;
  /** Open a full page for a row instead of the side drawer (e.g. the customer 360). */
  rowHref?(row: Record<string, unknown>): string;
  /** One settings-like record (GET + PUT/PATCH on `path`) rather than a list. */
  singleton?: boolean;
}

const full = (p: string) => `/v1/${p.replace(/^\/?(v1\/)?/, '')}`;

/** Sub-collections of an item: GET `<path>/{id}/<segment>` endpoints (health records of a horse, waivers of a person…). */
function subLists(itemPath: string): string[] {
  const base = full(itemPath);
  return Object.keys(spec.paths)
    .filter((p) => p.startsWith(`${base}/`) && /^\/[a-z0-9-]+$/.test(p.slice(base.length)) && spec.paths[p].get)
    .map((p) => p.slice(base.length + 1));
}

export function ResourcePage({ config }: { config: ResourceConfig }) {
  if (config.singleton) return <SingletonPage config={config} />;
  return <ListPage config={config} />;
}

function ListPage({ config }: { config: ResourceConfig }) {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Query>({ ...defaultQuery(config.path), ...(config.defaultFilters ?? {}) });
  const list = useList<Record<string, unknown>>(config.path, filters);
  const createSchema = config.hideCreate ? null : requestSchema('post', config.path);
  const [creating, setCreating] = useState(false);
  const selectedId = params.get('id');
  const selected = list.rows.find((r) => String(r.id) === selectedId) ?? (selectedId ? { id: selectedId } : null);
  const columns = useMemo(() => config.columns ?? inferColumns(list.rows), [config.columns, list.rows]);

  return (
    <div className="animate-fade-in">
      <PageHeader
        title={t(config.title)}
        description={config.description}
        actions={
          <>
            {config.headerActions}
            {createSchema && (
              <Button onClick={() => setCreating(true)}>
                <Plus /> {config.createLabel ?? `${t('New')}`}
              </Button>
            )}
          </>
        }
      />
      <FiltersBar path={config.path} value={filters} onChange={setFilters} context={config.path} />
      <DataTable
        rows={list.rows}
        columns={columns}
        loading={list.isLoading}
        context={config.path}
        selected={selectedId}
        onRowClick={(row) => row.id && (config.rowHref ? navigate(config.rowHref(row)) : setParams({ id: String(row.id) }))}
        empty={<EmptyState title={list.error ? errorMessage(list.error) : 'Nothing here yet'} description={list.error ? undefined : config.emptyHint ?? (createSchema ? 'Create the first one with the button above.' : undefined)} />}
        footer={<LoadMore hasMore={list.hasMore} loading={list.loadingMore} onClick={list.loadMore} />}
      />
      {createSchema && <FormSheet open={creating} onOpenChange={setCreating} title={`${t('New')} · ${config.title}`} schema={createSchema} method="post" path={config.path} context={config.path} overrides={config.overrides} idempotent={idempotent('post', config.path)} onSaved={(r) => r && typeof r === 'object' && 'id' in (r as object) && (config.rowHref ? navigate(config.rowHref(r as Record<string, unknown>)) : setParams({ id: String((r as { id: string }).id) }))} />}
      {selected && <RecordSheet config={config} row={selected} onClose={() => setParams({})} />}
    </div>
  );
}

export function FormSheet({ open, onOpenChange, title, schema, method, path, context, overrides, record, idempotent: idem, onSaved }: { open: boolean; onOpenChange(o: boolean): void; title: ReactNode; schema: NonNullable<ReturnType<typeof requestSchema>>; method: 'post' | 'patch' | 'put'; path: string; context: string; overrides?: Record<string, FieldOverride>; record?: Record<string, unknown> | null; idempotent?: boolean; onSaved?(result: unknown): void }) {
  const { t, label } = useI18n();
  const qc = useQueryClient();
  const [values, setValues] = useState<FormValues>(() => initialValues(schema, record));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setValues(initialValues(schema, record));
      setErrors({});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const errs = validate(schema, values, label);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const headers = record && typeof record.version === 'number' && method !== 'post' ? { 'If-Match': String(record.version) } : undefined;
      const result = await api[method](path, cleanValues(schema, values), { idempotent: idem, headers });
      toast.success(t('Saved'));
      void qc.invalidateQueries();
      onOpenChange(false);
      onSaved?.(result);
    } catch (e) {
      if (e instanceof ApiError) setErrors(e.fieldErrors());
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      width="lg"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('Cancel')}
          </Button>
          <Button loading={busy} onClick={() => void save()}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <SchemaForm schema={schema} value={values} onChange={setValues} errors={errors} context={context} overrides={overrides} />
      </form>
    </Sheet>
  );
}

const DETAIL_HIDDEN = new Set(['tenantId', 'deletedAt']);

/** A single configuration record: read it as a card, edit it with the contract's PUT/PATCH form. */
function SingletonPage({ config }: { config: ResourceConfig }) {
  const { t, label } = useI18n();
  const q = useQuery({ queryKey: [config.path], queryFn: () => api.get<Record<string, unknown>>(config.path) });
  const method = hasOperation('put', config.path) ? 'put' : hasOperation('patch', config.path) ? 'patch' : null;
  const schema = method ? requestSchema(method, config.path) : null;
  const [editing, setEditing] = useState(false);
  const record = q.data;
  const fields = record ? Object.entries(record).filter(([k]) => !DETAIL_HIDDEN.has(k) && !['id', 'createdAt', 'version'].includes(k)) : [];
  return (
    <div className="animate-fade-in">
      <PageHeader
        title={t(config.title)}
        description={config.description}
        actions={
          schema &&
          record && (
            <Button onClick={() => setEditing(true)}>
              <Pencil /> {t('Edit')}
            </Button>
          )
        }
      />
      <div className="card-surface p-6">
        {q.isLoading ? (
          <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : q.error ? (
          <EmptyState title={errorMessage(q.error)} />
        ) : (
          <DescriptionList columns={2} items={fields.map(([k, v]) => ({ label: label(k), value: <Value k={k} v={v} context={config.path} row={record} />, span: Boolean(v && typeof v === 'object' && !Array.isArray(v)) }))} />
        )}
      </div>
      {schema && method && record && <FormSheet open={editing} onOpenChange={setEditing} title={`${t('Edit')} · ${config.title}`} schema={schema} method={method} path={config.path} context={config.path} overrides={config.overrides} record={record} />}
    </div>
  );
}

export function RecordSheet({ config, row, onClose }: { config: ResourceConfig; row: Record<string, unknown>; onClose(): void }) {
  const { t, label } = useI18n();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const itemPath = config.itemPath ?? `${config.path}/{id}`;
  const id = String(row.id);
  const path = fillPath(itemPath, { id });
  const canGet = hasOperation('get', itemPath);
  const detail = useQuery({ queryKey: [path], queryFn: () => api.get<Record<string, unknown>>(path), enabled: canGet, retry: false });
  const record = (canGet ? detail.data : row) ?? row;
  const flat = (record.horse && typeof record.horse === 'object' ? { ...(record.horse as object), ...record } : record) as Record<string, unknown>;
  const updateMethod = config.hideEdit ? null : hasOperation('patch', itemPath) ? 'patch' : hasOperation('put', itemPath) ? 'put' : null;
  const updateSchema = updateMethod ? requestSchema(updateMethod, itemPath) : null;
  const canDelete = !config.hideDelete && hasOperation('delete', itemPath);
  const archives = deleteIsArchive(itemPath);
  const actions = itemActions(config.path, config.excludeActions ?? []);
  const subs = subLists(itemPath).filter((s) => !actions.some((a) => a.verb === s && a.method === 'put'));
  const [editing, setEditing] = useState(false);
  const refresh = () => void qc.invalidateQueries({ queryKey: [path] });

  const remove = async () => {
    const confirmed = archives
      ? await confirm({ title: 'Archive?', description: 'It stops being offered but stays in history and reports. Switch it back to active by editing it.', confirmLabel: 'Archive' })
      : await confirm({ title: `${t('Delete')}?`, description: 'This cannot be undone.', destructive: true, confirmLabel: t('Delete') });
    if (!confirmed) return;
    try {
      await api.delete(path);
      toast.success(archives ? 'Archived' : 'Deleted');
      void qc.invalidateQueries();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const title = config.rowTitle?.(flat) ?? String(flat.name ?? flat.nameEn ?? flat.titleEn ?? flat.title ?? flat.reference ?? flat.number ?? (flat.firstName ? `${flat.firstName} ${flat.lastName ?? ''}` : humanize(config.title)));
  const fields = Object.entries(flat).filter(([k, v]) => !DETAIL_HIDDEN.has(k) && !(Array.isArray(v) && v.length && typeof v[0] === 'object') && !(v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v as object).length > 6));
  const nested = Object.entries(flat).filter(([k, v]) => !DETAIL_HIDDEN.has(k) && Array.isArray(v) && v.length > 0 && typeof v[0] === 'object');

  return (
    <Sheet
      open
      onOpenChange={(o) => !o && onClose()}
      title={title || config.title}
      description={<span className="font-mono text-xs">{id}</span>}
      width="lg"
      headerExtra={
        <>
          {updateSchema && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil /> {t('Edit')}
            </Button>
          )}
          {canDelete && (
            archives ? (
              <Button variant="outline" size="sm" onClick={() => void remove()} disabled={flat.active === false}>
                <Archive /> {flat.active === false ? 'Archived' : 'Archive'}
              </Button>
            ) : (
              <Button variant="ghost" size="icon-sm" onClick={() => void remove()} aria-label="Delete">
                <Trash2 className="text-destructive" />
              </Button>
            )
          )}
        </>
      }
    >
      {actions.length > 0 && (
        <div className="mb-5 flex flex-wrap gap-2">
          {actions.map((a) => (
            <ActionButton key={`${a.method}-${a.verb}`} action={a} params={{ id }} context={config.path} onDone={refresh} />
          ))}
        </div>
      )}
      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">{t('Details')}</TabsTrigger>
          {nested.map(([k]) => (
            <TabsTrigger key={k} value={`n-${k}`}>
              {label(k)} <Badge className="ms-1">{(flat[k] as unknown[]).length}</Badge>
            </TabsTrigger>
          ))}
          {subs.map((s) => (
            <TabsTrigger key={s} value={`s-${s}`}>
              {humanize(s.replace(/-/g, '_'))}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="details">
          {canGet && detail.isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-5" />
              ))}
            </div>
          ) : (
            <>
              {config.renderDetail?.(flat, refresh)}
              <DescriptionList items={fields.map(([k, v]) => ({ label: label(k), value: <Value k={k} v={v} context={config.path} row={flat} />, span: typeof v === 'string' && v.length > 80 }))} />
            </>
          )}
        </TabsContent>
        {nested.map(([k]) => (
          <TabsContent key={k} value={`n-${k}`}>
            <DataTable rows={flat[k] as Array<Record<string, unknown>>} context={config.path} dense />
          </TabsContent>
        ))}
        {subs.map((s) => (
          <TabsContent key={s} value={`s-${s}`}>
            <SubList path={`${path}/${s}`} />
          </TabsContent>
        ))}
      </Tabs>
      {updateSchema && updateMethod && <FormSheet open={editing} onOpenChange={setEditing} title={`${t('Edit')} · ${title}`} schema={updateSchema} method={updateMethod} path={path} context={config.path} overrides={config.overrides} record={flat} />}
    </Sheet>
  );
}

function SubList({ path }: { path: string }) {
  const q = useQuery({ queryKey: [path], queryFn: () => api.get<unknown>(path) });
  if (q.isLoading) return <Skeleton className="h-24" />;
  if (q.error) return <EmptyState title={errorMessage(q.error)} />;
  const data = q.data;
  const list = toRows<Record<string, unknown>>(data);
  if (Array.isArray(data) || (data && typeof data === 'object' && 'data' in (data as object))) return <DataTable rows={list} context={path} dense />;
  if (data && typeof data === 'object') {
    return <DescriptionList items={Object.entries(data as Record<string, unknown>).filter(([k]) => !DETAIL_HIDDEN.has(k)).map(([k, v]) => ({ label: humanize(k), value: Array.isArray(v) && v.length && typeof v[0] === 'object' ? <DataTable rows={v as Array<Record<string, unknown>>} dense /> : <Value k={k} v={v} context={path} /> , span: Array.isArray(v) }))} />;
  }
  return <EmptyState title="Nothing here yet" />;
}

export function useCan(permission?: string) {
  const { can } = useAuth();
  return permission ? can(permission) : true;
}
